import React from 'react';
import { node, oneOf, bool, string } from 'prop-types';
import * as S from './styles';

const STATES = {
  VISIBLE: 'VISIBLE',
  TRANSITIONING_TO_HIDDEN: 'TRANSITIONING_TO_HIDDEN',
  HIDDEN: 'HIDDEN',
  TRANSITIONING_TO_VISIBLE: 'TRANSITIONING_TO_VISIBLE',
};

class HideableBox extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      state:
        props.visible && !props.initialTransition
          ? STATES.VISIBLE
          : STATES.HIDDEN,
      containerRef: React.createRef(),
    };

    this.onTransitionEnd = ev => {
      if (ev.currentTarget !== this.state.containerRef.current) {
        return;
      }

      switch (this.state.state) {
        case STATES.TRANSITIONING_TO_HIDDEN:
          this.setState({ state: STATES.HIDDEN });
          break;
        case STATES.TRANSITIONING_TO_VISIBLE:
          this.setState({ state: STATES.VISIBLE });
          break;
        default:
          break;
      }
    };
  }

  static forceRenderWithCurrentHeight(el) {
    // eslint-disable-next-line no-param-reassign
    el.style.height = `${el.offsetHeight}px`;
    // We need to read the offsetHeight in order to force a reflow
    return el.offsetHeight;
  }

  static forceRender(el) {
    // We need to read the offsetHeight in order to force a reflow
    return el.offsetHeight;
  }

  static needsManualTransitionEventDispatch({ fade, nudge, slide, collapse }) {
    // If no property is transitioned, no transitionend event will be triggered
    // by the browser
    return !(fade || nudge || slide || collapse);
  }

  static dispatchTransitionEndEvent(el) {
    if (!el) {
      // This happens when the component is unmounted before the transition ends
      return;
    }
    const ev = new Event('transitionend', { bubbles: true });
    el.dispatchEvent(ev);
  }

  static getDerivedStateFromProps(props, componentState) {
    const { speed, collapse, delayIn, delayOut } = props;
    const { state, containerRef } = componentState;

    if (
      !HideableBox.getVisibility(props, componentState) &&
      state === STATES.VISIBLE
    ) {
      if (speed === S.SPEEDS.INSTANT) {
        return { state: STATES.HIDDEN };
      }

      if (collapse) {
        HideableBox.forceRenderWithCurrentHeight(containerRef.current);
      }

      if (HideableBox.needsManualTransitionEventDispatch(props)) {
        setTimeout(
          () => HideableBox.dispatchTransitionEndEvent(containerRef.current),
          S.DELAY_TO_DURATION_MS[delayIn] + S.SPEED_TO_DURATION_MS[speed]
        );
      }

      return { state: STATES.TRANSITIONING_TO_HIDDEN };
    }

    if (
      HideableBox.getVisibility(props, componentState) &&
      state === STATES.HIDDEN
    ) {
      if (speed === S.SPEEDS.INSTANT) {
        return { state: STATES.VISIBLE };
      }

      if (collapse) {
        HideableBox.forceRenderWithCurrentHeight(containerRef.current);
      }

      if (HideableBox.needsManualTransitionEventDispatch(props)) {
        setTimeout(
          () => HideableBox.dispatchTransitionEndEvent(containerRef.current),
          S.DELAY_TO_DURATION_MS[delayOut] + S.SPEED_TO_DURATION_MS[speed]
        );
      }

      return { state: STATES.TRANSITIONING_TO_VISIBLE };
    }

    return null;
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.state === STATES.HIDDEN &&
      this.state.state !== STATES.HIDDEN
    ) {
      // The first time the component goes from hidden to not hidden, the
      // children have not yet been rendered, so the height measured by
      // getContainerStyle will be 0. Therefore, we need to force a second
      // render, in order to measure the component with rendered children.
      this.forceUpdate();
    }
  }

  onContainerRefChanged = element => {
    const { initialTransition } = this.props;
    const { containerRef } = this.state;

    containerRef.current = element;

    if (initialTransition && element) {
      // Force a DOM reflow to make sure we have an initial state to transition from.
      HideableBox.forceRender(containerRef.current);
      // Force an update on the next animation frame to make sure the transition is triggered.
      requestAnimationFrame(() => this.forceUpdate());
    }
  };

  static getVisibility({ visible, initialTransition }, { containerRef }) {
    if (!visible) {
      return false;
    }

    // If the initialTransition flag is set, the target visibility should be hidden until we have the containerRef.
    return !initialTransition || containerRef.current;
  }

  static getContainerStyle(collapse, state, containerRef) {
    if (collapse !== S.AXES.VERTICAL) {
      return undefined;
    }

    switch (state) {
      case STATES.TRANSITIONING_TO_HIDDEN:
        return { height: '0px', overflow: 'hidden' };
      case STATES.TRANSITIONING_TO_VISIBLE:
        return {
          height: `${containerRef.current.scrollHeight}px`,
          overflow: 'hidden',
        };
      default:
        return undefined;
    }
  }

  render() {
    const {
      className,
      children,
      collapse,
      renderHiddenChildren,
      visible,
      ...styledComponentProps
    } = this.props;

    const { state, containerRef } = this.state;

    const shouldRenderChildren =
      state !== STATES.HIDDEN || renderHiddenChildren;

    return (
      <S.HideableBox
        className={className}
        {...styledComponentProps}
        visible={HideableBox.getVisibility(this.props, this.state)}
        onTransitionEnd={this.onTransitionEnd}
        ref={this.onContainerRefChanged}
        style={HideableBox.getContainerStyle(collapse, state, containerRef)}
      >
        {shouldRenderChildren && children}
      </S.HideableBox>
    );
  }
}

export const { SPEEDS, DIRECTIONS, AXES, DELAYS } = S;

HideableBox.propTypes = {
  children: node,
  renderHiddenChildren: bool,
  visible: bool,
  speed: oneOf(Object.values(SPEEDS)),
  delayIn: oneOf(Object.values(DELAYS)),
  delayOut: oneOf(Object.values(DELAYS)),
  fade: bool,
  nudge: oneOf(Object.values(DIRECTIONS)),
  nudgeAmount: string,
  slide: oneOf(Object.values(DIRECTIONS)),
  collapse: oneOf(Object.values(AXES)),
  initialTransition: bool,
  className: string,
};

HideableBox.defaultProps = {
  children: null,
  visible: true,
  speed: SPEEDS.FAST,
  delayIn: DELAYS.NONE,
  delayOut: DELAYS.NONE,
  fade: true,
  nudge: undefined,
  nudgeAmount: '4px',
  slide: undefined,
  collapse: undefined,
  renderHiddenChildren: false,
  initialTransition: false,
  className: undefined,
};

export default HideableBox;
